import useExperiment from 'hooks/common/use-experiment';
import { ShopifyImage } from 'interfaces/product';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { getCleanPath } from 'utils/collection';
import { IMAGES_CONFIG } from './config';
import { insertImage } from './utils';

interface UseTest7995Props {
  originalImages: ShopifyImage[];
  productTitle: string;
}

interface UseTest7995Return {
  images: ShopifyImage[];
}

/**
 * Custom hook for PT-7995 A/B test: Mood & USP image test
 * @see https://petsdeli.atlassian.net/browse/PT-7995
 *
 * Handles the injection of mood/USP images at specified positions
 */
export const useTest7995 = ({
  originalImages,
  productTitle,
}: UseTest7995Props): UseTest7995Return => {
  const router = useRouter();

  const { variant, loading: loadingTest } = useExperiment({
    baseEntryId: '3cQVERZu4IKjfiaW3aeBo4',
  });

  const images = useMemo(() => {
    // Get current product handle from router, removing query parameters
    const currentPath = getCleanPath(router.asPath);
    const configImages = IMAGES_CONFIG[currentPath];

    // Check if test exists or is still loading
    // Check if test is still loading or configImages is undefined
    if (loadingTest || configImages === undefined || variant !== 1) {
      return originalImages;
    }

    return configImages.reduce(
      (acc, item) =>
        insertImage({
          target: acc,
          configImage: item,
          alt: `Mood image for ${productTitle}`,
        }),
      [...originalImages]
    );
  }, [loadingTest, originalImages, productTitle, router.asPath, variant]);

  return {
    images,
  };
};

export default useTest7995;
